import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { trackException } from 'app-insights/appInsightsTracking';
import {
  selectPlacementHeaderData,
  selectPlacementOrderType,
} from '../../../../../store/redux-store/placement-details/selectors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormControl, Select as AMNSelect, MenuItem } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { colors } from 'styles/styleVariables';
import {
  IPlacementStatus,
  IPlacementStatusSource,
  IPlacementStatusSuccessType,
  MiscPlacementDialogType,
  PlacementOrderType,
} from 'app/models/Placement/PlacementDetails';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { usePromiseTracker } from 'react-promise-tracker';
import { pageAttributesActions } from '../Edit/store/placementPageAttributes.redux';
import styledComponent from 'styled-components';
import { useTranslation } from 'react-i18next';
import { StatusChangeConfirmDialog } from '../../NextPlacementStatus/StatusChangeConfirmDialog';
import { placementStatusAction } from '../../../../../store/redux-store/placement-status/slice';
import { selectPlacementStatus } from '../../../../../store/redux-store/placement-status/selectors';
import { useDecision } from '@optimizely/react-sdk';
import {
  ff_placementStatusChangeKey,
  ff_placement_ui_requirement_check,
} from 'app/constants/FeatureFlags/Placement/Keys';
import {
  isInstanceOfExtensionStatus,
  useRequirementPromiseTracker,
} from '../PlacementTabPanel/PlacementSummaryTab/helper';
import { usePlacementStatusDisable } from '../../NextPlacementStatus/Helper';
import {
  isBooked,
  isBookingApproved,
  isCancellationStatus,
  isClientConfirmation,
  isGoodToGo,
  isInterviewScheduled,
  isInterviewed,
  isOfferBookedByTraveler,
  isOfferByFacility,
  isOnAssignment,
  isRFO,
  isRequestContract,
  isSent,
  isSubmitPacket,
  isTA,
  isWFC,
  isWaitingGoodToGo,
  placementStatusOptions,
} from '../../NextPlacementStatus/StatusDefaults';
import { SubmitPacketDialog } from '../../NextPlacementStatus/Packets/SubmitPacketDialog';
import { removeSessionValue, SessionKey, StorageType } from 'utils/customHooks/sessionStorage/sessionHelpers';
import { CustomTooltip } from 'app/components/Common/Tooltips/CustomTooltip';
import { selectAutomationStatus } from '@AMIEWEB/Order/OrderDetails/OrderPreferences/store/OrderPreference.selector';
import { selectPlacementSummary } from 'store/redux-store/placement-summary/selectors';
import { ClientConfirmationDialog } from '@AMIEWEB/Placement/NextPlacementStatus/ClientConfirmation/ClientConfirmationDialog';
import { MiscDialogues } from '@AMIEWEB/Placement/MiscDialogues';

const useStyles = makeStyles()(theme => ({
  placementStatusChangeDDIcon: {
    color: 'white !important',
    fontSize: '1.3rem',
  },
  placementStatusMenuItem: {
    fontSize: 14,
    height:'40px',
    '&:hover': {
      backgroundColor: theme.palette.components.buttonDropdown.onHover.backgroundColor,
    },
  },
  placementStatusInput: {
    textTransform: 'uppercase',
    fontSize: '12px',
  },
  dropdownStyle: {
    marginTop: '1px',
  },
  root: {
    color: 'white',
    height: '23px !important',
    paddingTop: '5px !important',
    paddingBottom: '0 !important',
    '&:hover': {
      backgroundColor: colors.amnDeeperBlue,
    },
  },
  rootDisabled: {
    color: 'white !important',
    '-webkit-text-fill-color': 'white !important',
    '&:hover': {
      cursor: 'not-allowed !important',
    },
  },
}));

export const PlacementDetailsStatus = () => {
  const dispatch = useDispatch();
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { control, getValues, formState: { dirtyFields } } = useFormContext();
  const { placementId, candidate, brandId, isArchived } = useSelector(selectPlacementHeaderData);
  const params = useParams<{ placementId: string }>();
  const routePlacementId = Number(params?.placementId);
  // While prospecting (up/down) navigation is in flight the URL can point at a new placement
  // before the header slice has loaded it. Treat that transient window as "not ready".
  const isPlacementMismatch = Number.isInteger(routePlacementId) && placementId !== routePlacementId;
  const {
    availablePlacementStatus,
    currentPlacementStatus,
    updatePlacementStatusResponse,
    failedRequirements,
    isModificationError,
  } = useSelector(selectPlacementStatus);
  const { selectedStatus } = useSelector(selectPlacementStatus);
  const loadingRequirements = useRequirementPromiseTracker();
  const [decision] = useDecision(ff_placementStatusChangeKey, { autoUpdate: true });
  const [requirementCheckFlag] = useDecision(ff_placement_ui_requirement_check);
  const { promiseInProgress: loadingPackets } = usePromiseTracker({
    area: 'get-placement-submit-packet-documents',
    delay: 0,
  });
  const { promiseInProgress: loadingConfirmationDoc } = usePromiseTracker({
    area: 'get-placement-client-confirmation-documents',
    delay: 0,
  });

  const { promiseInProgress: submitting } = usePromiseTracker({ area: 'update-placement-status', delay: 0 });
  const { promiseInProgress: loadingStatusDetails } = usePromiseTracker({
    area: 'get-placement-status-details',
    delay: 0,
  });
  const { promiseInProgress: submittingExtensionStatus } = usePromiseTracker({
    area: 'update-extension-status',
    delay: 0,
  });

  const _onChange = event => {
    // Change dialog is shown from WhatsNext container when redux status is set to a value
    if (event.target.value) {
      const status = JSON.parse(event.target.value as string);
      isOnAssignment(currentPlacementStatus) && isCancellationStatus(status)
        ? dispatch(
            pageAttributesActions.setMiscDialog({
              key: MiscPlacementDialogType.on_assignment_confirmation_cancellation,
              data: { status, statusChangeSource: 'statusDropDown' },
            }),
          )
        : dispatch(
            placementStatusAction.setSelectedStatus({
              status,
              statusChangeSource: 'statusDropDown',
            }),
          );
    }
  };

  const onCloseDialog = React.useCallback(() => {
    removeSessionValue(SessionKey.createPlacementMFS, StorageType.sessionStorage);
    if (updatePlacementStatusResponse?.successType !== IPlacementStatusSuccessType.placementConflict) {
      dispatch(placementStatusAction.setUpdatePlacementStatusResponse(null));
      dispatch(placementStatusAction.setIsModificationError({ isModificationError: false }));
    }
    dispatch(placementStatusAction.setSelectedStatus({ status: undefined }));
  }, [dispatch, updatePlacementStatusResponse]);

  const resetError = () => {
    if (updatePlacementStatusResponse?.successType !== IPlacementStatusSuccessType.placementConflict)
      dispatch(
        placementStatusAction.setUpdatePlacementStatusResponse({
          success: undefined,
          source: IPlacementStatusSource.statusConfirmationDialog,
          successType: IPlacementStatusSuccessType.placementUpdate,
        }),
      );
  };

  React.useEffect(() => {
    if (updatePlacementStatusResponse?.success && Number.isInteger(placementId) && placementId > 0) {
      dispatch(pageAttributesActions.getEditableStatusesAction({ placementId: placementId }));
      dispatch(
        globalActions.setSnackBar({
          severity: 'success',
          message: !isArchived
            ? t('placement.profile.placementStatusUpdate.successSnackbar')
            : t('placement.profile.placementStatusUpdate.archiveSnackbar'),
        }),
      );
      if (updatePlacementStatusResponse?.supportAPIFailure) {
        dispatch(
          globalActions.setSnackBar({
            severity: 'error',
            message: t('placement.profile.placementStatusUpdate.supportAPIFailure'),
          }),
        );
      }
      onCloseDialog();
    }
  }, [dispatch, onCloseDialog, placementId, t, updatePlacementStatusResponse, isArchived]);

  const onSubmit = React.useCallback(
    (status: IPlacementStatus) => {
      if (selectedStatus && Number.isInteger(placementId) && placementId > 0) {
        // Guard against cross-placement status writes: the header placement must match the
        // placement currently in the URL, otherwise a status change validated against the
        // previously viewed placement could be committed to a different placement id.
        if (Number.isInteger(routePlacementId) && placementId !== routePlacementId) {
          trackException({
            exception: new Error('Aborted placement status update: URL/header placement id mismatch'),
            properties: {
              name: 'PlacementStatusCrossWriteGuard',
              functionName: 'onSubmit',
              area: 'PlacementDetailsStatus.tsx',
              urlPlacementId: routePlacementId,
              headerPlacementId: placementId,
            },
          });
          return;
        }
        isInstanceOfExtensionStatus(status)
          ? dispatch(placementStatusAction.updateExtentionStatus({ placementId, ...status }))
          : isRequestContract(status)
          ? dispatch(placementStatusAction.createClassicRequestContract({ placementId }))
          : dispatch(
              placementStatusAction.updatePlacementStatusAction({
                placementStatus: status,
                candidateId: candidate?.id,
                brandId: brandId,
                placementUpdate: {
                  placementId,
                  dirtyFields: dirtyFields,
                  updates: getValues(),
                  validateConflict: {
                    source: IPlacementStatusSource.statusConfirmationDialog,
                  },
                  requirementCheckFlagEnabled: requirementCheckFlag.enabled,
                },
              }),
            );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brandId, candidate?.id, dirtyFields, placementId, routePlacementId, selectedStatus],
  );

  /**
   * Custom filter for the statuses in the dropdown menu
   */
  const filteredAvailablePlacementStatus = React.useMemo(() => {
    return availablePlacementStatus.filter(item => {
      if (item.activityStatus === placementStatusOptions.submitPacket.activityStatus) {
        return false;
      }

      switch (item.activityTypeId) {
        case 1:
          switch (item.activityStatusId) {
            default:
              return true;
          }
        case 101:
          switch (item.activityStatusId) {
            case placementStatusOptions.submitPacket.activityStatusId:
            case placementStatusOptions.archivePlacement.activityStatusId:
            case placementStatusOptions.clientConfirmation.activityStatusId:
            case placementStatusOptions.candidateContract.activityStatusId:
            case placementStatusOptions.requestContract.activityStatusId:
              return false;
            default:
              return true;
          }
        default:
          return true;
      }
    });
  }, [availablePlacementStatus]);

  return (
    <React.Fragment>
      <Controller
        control={control}
        name="currentPlacementStatus"
        render={({ field }) => (
          // If this needs to integrate with react hook form, call onChange from onChange handler
          <React.Fragment>
            {currentPlacementStatus && (
              <FormControl color="primary" size="small" variant="outlined">
                <Select
                  IconComponent={ExpandMoreIcon}
                  classes={{
                    disabled: classes.rootDisabled,
                    select: cx(classes.root, {
                      [classes.rootDisabled]:
                        !decision.enabled ||
                        loadingRequirements ||
                        loadingStatusDetails ||
                        isPlacementMismatch ||
                        (filteredAvailablePlacementStatus?.length || 0) === 0,
                    }),
                    icon: classes.placementStatusChangeDDIcon,
                  }}
                  inputProps={{
                    className: classes.placementStatusInput,
                  }}
                  MenuProps={{
                    classes: { paper: classes.dropdownStyle },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  }}
                  variant="outlined"
                  color="primary"
                  disabled={
                    !decision.enabled ||
                    loadingRequirements ||
                    loadingStatusDetails ||
                    isPlacementMismatch ||
                    (filteredAvailablePlacementStatus?.length || 0) === 0
                  }
                  value={JSON.stringify(currentPlacementStatus)}
                  onChange={_onChange}
                >
                  <MenuItem
                    className={classes.placementStatusMenuItem}
                    style={{ display: 'none' }}
                    key={JSON.stringify(currentPlacementStatus)}
                    value={JSON.stringify(currentPlacementStatus)}
                  >
                    {currentPlacementStatus.activityStatus}
                  </MenuItem>
                  {filteredAvailablePlacementStatus.map((status, i) => (
                    <StatusMenuItem
                      status={status}
                      key={JSON.stringify(status)}
                      value={JSON.stringify(status)}
                      hasFailedRequirements={
                        ((failedRequirements || {})[`${status.activityTypeId}:${status.activityStatusId}`]?.length ||
                          0) > 0
                      }
                      index={i}
                    />
                  ))}
                </Select>
              </FormControl>
            )}
          </React.Fragment>
        )}
      />
      {selectedStatus &&
        (isSubmitPacket(selectedStatus) ? (
          <SubmitPacketDialog
            open
            onClose={onCloseDialog}
            loading={loadingPackets}
            submitting={submitting}
            markPlacementAsSent={onSubmit}
            resetError={resetError}
            error={updatePlacementStatusResponse && updatePlacementStatusResponse.success === false}
          />
        ) : isClientConfirmation(selectedStatus) ? (
          <ClientConfirmationDialog
            open
            onClose={onCloseDialog}
            loading={loadingConfirmationDoc}
            error={updatePlacementStatusResponse && updatePlacementStatusResponse.success === false}
          />
        ) : (
          <StatusChangeConfirmDialog
            open
            status={selectedStatus}
            submitting={submitting || submittingExtensionStatus}
            error={updatePlacementStatusResponse && updatePlacementStatusResponse.success === false}
            type={updatePlacementStatusResponse?.successType}
            onSubmit={onSubmit}
            onClose={onCloseDialog}
            isModificationError={isModificationError}
            errorMessage={updatePlacementStatusResponse?.errorMessage}
          />
        ))}
      <MiscDialogues />
    </React.Fragment>
  );
};

interface StatusMenuItemProps {
  status: any;
  hasFailedRequirements: boolean;
  index: number;
  key: any;
  value: any;
}
const StatusMenuItem = React.forwardRef<HTMLLIElement, StatusMenuItemProps>((props: StatusMenuItemProps, ref) => {
  const { status, hasFailedRequirements, index, ...rest } = props;
  const { classes } = useStyles();
  const placementOrderType = useSelector(selectPlacementOrderType);
  const { currentPlacementStatus, failedRequirements } = useSelector(selectPlacementStatus);
  const [allowManualStatusUpdate, setAllowManualStatusUpdate] = useState(true);
  const isSubmissionAutomationEnabled = useSelector(selectAutomationStatus);

  const {
    orderInformation: { positionsAvailable },
  } = useSelector(selectPlacementSummary);

  const createScreenEnabled = useMemo(() => positionsAvailable === 0, [positionsAvailable]);

  useEffect(() => {
    if (placementOrderType === PlacementOrderType.StrikeOrderInHsg) {
      setAllowManualStatusUpdate(true);
    }
    if (
      (isSent(currentPlacementStatus) ||
        isInterviewed(currentPlacementStatus) ||
        isInterviewScheduled(currentPlacementStatus)) &&
      (isOfferByFacility(status) || isOfferBookedByTraveler(status)) &&
      hasFailedRequirements
    )
      setAllowManualStatusUpdate(false);
    else if (isOfferByFacility(currentPlacementStatus) && isTA(status) && hasFailedRequirements)
      setAllowManualStatusUpdate(false);
    else if (isWFC(currentPlacementStatus) && isTA(status) && (hasFailedRequirements || isSubmissionAutomationEnabled))
      setAllowManualStatusUpdate(false);
    else if (isWaitingGoodToGo(currentPlacementStatus) && isGoodToGo(status) && hasFailedRequirements)
      setAllowManualStatusUpdate(false);
    else if (isTA(currentPlacementStatus) && isWaitingGoodToGo(status) && hasFailedRequirements)
      setAllowManualStatusUpdate(false);
    else if (isWFC(currentPlacementStatus) && isRFO(status) && hasFailedRequirements) setAllowManualStatusUpdate(false);
    else if ((isBooked(status) || isBookingApproved(status) || isWaitingGoodToGo(status)) && hasFailedRequirements)
      setAllowManualStatusUpdate(false);
    else if (isGoodToGo(currentPlacementStatus) && isOnAssignment(status) && hasFailedRequirements)
      setAllowManualStatusUpdate(false);
    else setAllowManualStatusUpdate(true);
  }, [
    currentPlacementStatus,
    currentPlacementStatus.activityStatusId,
    hasFailedRequirements,
    isSubmissionAutomationEnabled,
    placementOrderType,
    status,
  ]);

  //usePlacementStatusDisable used in picklist in placement details
  const { disabled, toolTipContent } = usePlacementStatusDisable({
    status,
    hasFailedRequirements,
    manualAllowed: allowManualStatusUpdate,
    isStrike: placementOrderType === PlacementOrderType.StrikeOrderInHsg,
    failedRequirements: failedRequirements[`${status.activityTypeId}:${status.activityStatusId}`],
    allFailedRequirements: failedRequirements,
    createScreenEnabled: createScreenEnabled,
  });
  return (
    <CustomTooltip tooltipContent={toolTipContent} disabled={!disabled} placement={'left'} isStandardContent>
      <MenuItem
        ref={ref}
        disabled={disabled}
        id={`placement-status-menu-item-${index}`}
        className={classes.placementStatusMenuItem}
        {...rest}
      >
        {status?.activityStatus}
      </MenuItem>
    </CustomTooltip>
  );
});

const Select = styledComponent(AMNSelect)`
  background-color: ${colors.amnBlue};
  color: white;
`;
